/**
 * Barcode Scanner Component
 *
 * Uses @undecaf/barcode-detector-polyfill (ZBar C library compiled to WASM)
 * behind the standard W3C BarcodeDetector API.
 *
 * Why not html5-qrcode/ZXing-js: the JS port of ZXing produces phantom
 * decodes on Code 128 and barely handles GS1 DataBar Expanded. ZBar
 * enforces strict checksum + quiet-zone validation and has real DataBar
 * Expanded support.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BarcodeDetectorPolyfill } from '@undecaf/barcode-detector-polyfill';
import { setModuleArgs } from '@undecaf/zbar-wasm';
import zbarWasmUrl from '@undecaf/zbar-wasm/dist/zbar.wasm?url';
import { decodeMSIFromImageData } from '../utils/scanner/msi';
import { mapCoverBox } from '../utils/scanner/scanOverlay';

// #2210 — a decoded barcode's location in CAPTURE-canvas pixels, so the overlay
// can show the rep exactly where the scanner locked on (catch a wrong-tag /
// partial read before the value commits). Source distinguishes the painter color.
interface DecodeRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  source: 'zbar' | 'msi';
  value: string;
  at: number;
}

// Tell the ZBar Emscripten module where its WASM lives.
setModuleArgs({
  locateFile: (file: string) => (file.endsWith('.wasm') ? zbarWasmUrl : file),
});

// Narrowed to exactly the formats the rep workflow needs (#1872). The
// previous broader list (Code 39 / 93 / Codabar / ITF / DataBar / QR)
// let ZBar fire on too much non-relevant ink, including occasionally
// hallucinating decodes on dense text. UPC-A / UPC-E / EAN-13 cover
// products; Code 128 covers most internal SKUs; MSI Plessey is our new
// decoder running as a fallback in BarcodeScanner's detect loop for
// retailer shelf tags.
const DEFAULT_FORMATS = [
  'upc_a',
  'upc_e',
  'ean_13',
  'code_128',
];

// Quality floor for accepted decodes. ZBar emits a per-symbol quality
// score; values below this are treated as low-confidence phantom decodes
// and dropped. Raised from 15 → 30 after RMR saw phantom detections on
// printed text in the camera before any barcode was in view. ZBar
// rarely scores above 30 on accidental text; real barcodes score 50+.
const MIN_QUALITY = 30;

// Hard timeout on a single detect() call. If WASM never finishes
// loading (SW cache miss, broken MIME, etc) we'd otherwise be stuck
// with detectingRef pinned true and the loop never advances.
const DETECT_TIMEOUT_MS = 4000;

// Frame capture downscale. Detecting on a 1920x1080 frame is wasteful;
// 1280-wide is plenty for retail barcodes and much faster on iPhone.
const MAX_CAPTURE_WIDTH = 1280;

interface BarcodeScannerProps {
  onScan: (decodedText: string) => void;
  onError?: (error: string) => void;
  isActive?: boolean;
  width?: number;
  height?: number;
  formats?: string[];
  // Show on-screen diagnostic HUD with detector state and per-frame
  // stats. Off by default; opt in when diagnosing decode issues.
  debug?: boolean;
  // #2210 — draw a box over the video showing where the scanner locked on,
  // so the rep can confirm the right tag was read. On by default.
  decodeOverlay?: boolean;
}

type ScannerState = 'initializing' | 'scanning' | 'paused' | 'no_camera' | 'permission_denied' | 'error';

interface DebugStats {
  detectorReady: boolean;
  videoW: number;
  videoH: number;
  frames: number;
  attempts: number;
  lastError: string;
  lastFormat: string;
  lastQuality: number;
  lastValue: string;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  onError,
  isActive = true,
  width = 300,
  height = 200,
  formats = DEFAULT_FORMATS,
  debug = false,
  decodeOverlay = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // #2210 — overlay canvas + the most recent decode region (capture-canvas px).
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const decodeRegionRef = useRef<DecodeRegion | null>(null);
  const overlayRafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<BarcodeDetectorPolyfill | null>(null);
  const rafRef = useRef<number | null>(null);
  const activeRef = useRef(isActive);
  const mountedRef = useRef(true);
  const startedRef = useRef(false);
  const startingRef = useRef(false);
  const detectingRef = useRef(false);
  const lastScannedRef = useRef<string | null>(null);
  const lastScanTimeRef = useRef(0);
  // MSI cross-frame stability buffer (#1873): last N frames' decoded
  // digit strings. We only fire handleScan when >=2 of the last 3 agree.
  const msiHistoryRef = useRef<string[]>([]);

  const [state, setState] = useState<ScannerState>('initializing');
  const [errorMessage, setErrorMessage] = useState('');
  const [stats, setStats] = useState<DebugStats>({
    detectorReady: false,
    videoW: 0,
    videoH: 0,
    frames: 0,
    attempts: 0,
    lastError: '',
    lastFormat: '',
    lastQuality: 0,
    lastValue: '',
  });
  const statsRef = useRef(stats);
  statsRef.current = stats;

  const bumpStats = useCallback((patch: Partial<DebugStats>) => {
    setStats((s) => ({ ...s, ...patch }));
  }, []);

  const handleScan = useCallback(
    (value: string) => {
      const now = Date.now();
      if (value === lastScannedRef.current && now - lastScanTimeRef.current < 2000) {
        return;
      }
      lastScannedRef.current = value;
      lastScanTimeRef.current = now;
      onScan(value);
    },
    [onScan]
  );

  // Race detect() against a timeout so a hung WASM init doesn't pin the
  // loop indefinitely.
  const detectWithTimeout = useCallback(
    (
      source: HTMLCanvasElement | ImageData,
    ): Promise<
      Array<{
        format: string;
        rawValue: string;
        quality: number;
        boundingBox?: { x: number; y: number; width: number; height: number };
      }>
    > => {
      const detector = detectorRef.current;
      if (!detector) return Promise.resolve([]);
      return Promise.race([
        detector.detect(source as unknown as CanvasImageSource) as unknown as Promise<
          Array<{
            format: string;
            rawValue: string;
            quality: number;
            boundingBox?: { x: number; y: number; width: number; height: number };
          }>
        >,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`detect timeout > ${DETECT_TIMEOUT_MS}ms`)), DETECT_TIMEOUT_MS)
        ),
      ]);
    },
    []
  );

  const detectLoop = useCallback(async () => {
    rafRef.current = null;
    if (!mountedRef.current || !activeRef.current) {
      return;
    }
    const video = videoRef.current;
    const detector = detectorRef.current;
    if (!video || !detector) {
      rafRef.current = requestAnimationFrame(() => detectLoop());
      return;
    }
    // iOS Safari can return readyState 4 with videoWidth/Height still 0
    // until the first frame is fully decoded. Skip frames where the
    // video has no real pixels yet.
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw === 0 || vh === 0 || video.paused) {
      rafRef.current = requestAnimationFrame(() => detectLoop());
      return;
    }
    if (detectingRef.current) {
      rafRef.current = requestAnimationFrame(() => detectLoop());
      return;
    }
    detectingRef.current = true;

    // Draw current video frame to our own canvas at a sane size. This
    // removes HTMLVideoElement quirks from the equation and lets ZBar
    // see a stable, sized image every time.
    try {
      let canvas = canvasRef.current;
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvasRef.current = canvas;
      }
      const scale = vw > MAX_CAPTURE_WIDTH ? MAX_CAPTURE_WIDTH / vw : 1;
      const cw = Math.round(vw * scale);
      const ch = Math.round(vh * scale);
      if (canvas.width !== cw) canvas.width = cw;
      if (canvas.height !== ch) canvas.height = ch;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        detectingRef.current = false;
        rafRef.current = requestAnimationFrame(() => detectLoop());
        return;
      }
      ctx.drawImage(video, 0, 0, cw, ch);

      bumpStats({
        frames: statsRef.current.frames + 1,
        videoW: vw,
        videoH: vh,
      });

      const results = await detectWithTimeout(canvas);
      bumpStats({ attempts: statsRef.current.attempts + 1, lastError: '' });

      if (results.length > 0) {
        const best = results.reduce((a, b) => (a.quality > b.quality ? a : b));
        bumpStats({
          lastFormat: best.format,
          lastQuality: best.quality,
          lastValue: best.rawValue,
        });
        if (best.quality >= MIN_QUALITY && best.rawValue) {
          if (decodeOverlay && best.boundingBox) {
            const bb = best.boundingBox;
            decodeRegionRef.current = {
              x: bb.x,
              y: bb.y,
              w: bb.width,
              h: bb.height,
              source: 'zbar',
              value: best.rawValue,
              at: Date.now(),
            };
          }
          handleScan(best.rawValue);
        }
      } else {
        // ZBar found nothing in this frame. Try the proprietary MSI/Plessey
        // decoder (#1872) — ZBar has zero MSI support upstream, so retailer
        // shelf tags (Walgreens, CVS, etc.) require this fallback.
        //
        // Production stability: a single-frame MSI decode (even with
        // cross-scanline voting) can still false-positive on noise once
        // the gates were loosened (#1873). Require the SAME digit string
        // to appear in >=2 of the last 3 frames before firing handleScan.
        // Real barcodes held in frame produce consistent decodes; random
        // noise rarely repeats identical digits across frames.
        try {
          const imageData = ctx.getImageData(0, 0, cw, ch);
          const msi = decodeMSIFromImageData(imageData);
          if (msi && msi.digits) {
            const history = msiHistoryRef.current;
            history.push(msi.digits);
            if (history.length > 3) history.shift();
            const counts = new Map<string, number>();
            for (const v of history) counts.set(v, (counts.get(v) ?? 0) + 1);
            const stable = [...counts.entries()].find(([, n]) => n >= 2);

            bumpStats({
              lastFormat: `msi_${msi.checksum}`,
              lastQuality: Math.round(msi.confidence * 100),
              lastValue: stable ? msi.digits : `${msi.digits} (1/3)`,
            });

            if (stable) {
              if (decodeOverlay && msi.span) {
                // span is one scanline (x0..x1 at y) — give it a visible band.
                const x0 = Math.min(msi.span.x0, msi.span.x1);
                const bandH = Math.max(10, Math.round(ch * 0.07));
                decodeRegionRef.current = {
                  x: x0,
                  y: Math.max(0, msi.span.y - bandH / 2),
                  w: Math.abs(msi.span.x1 - msi.span.x0),
                  h: bandH,
                  source: 'msi',
                  value: stable[0],
                  at: Date.now(),
                };
              }
              msiHistoryRef.current = [];
              handleScan(stable[0]);
            }
          } else {
            // Clear stability buffer when no decode — prevents merging
            // decodes from different barcodes the rep panned across.
            if (msiHistoryRef.current.length > 0) msiHistoryRef.current = [];
          }
        } catch (msiErr) {
          // Don't let an MSI error abort the scan loop — log it for the
          // diagnostics overlay and keep ZBar running normally.
          const message = msiErr instanceof Error ? msiErr.message : String(msiErr);
          bumpStats({ lastError: `msi: ${message.slice(0, 60)}` });
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      bumpStats({ lastError: message.slice(0, 80) });
    } finally {
      detectingRef.current = false;
    }

    if (mountedRef.current && activeRef.current) {
      rafRef.current = requestAnimationFrame(() => detectLoop());
    }
  }, [bumpStats, detectWithTimeout, handleScan, decodeOverlay]);

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
      } catch {
        // ignore
      }
      video.srcObject = null;
    }
    startedRef.current = false;
  }, []);

  // Wait for the video to actually have pixels before declaring it ready.
  const waitForVideoFrames = useCallback((video: HTMLVideoElement): Promise<void> => {
    return new Promise((resolve) => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        resolve();
        return;
      }
      const check = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          video.removeEventListener('loadedmetadata', check);
          video.removeEventListener('loadeddata', check);
          video.removeEventListener('playing', check);
          resolve();
        }
      };
      video.addEventListener('loadedmetadata', check);
      video.addEventListener('loadeddata', check);
      video.addEventListener('playing', check);
      // Safety net: resolve after 3s even if no event fires.
      setTimeout(() => {
        video.removeEventListener('loadedmetadata', check);
        video.removeEventListener('loadeddata', check);
        video.removeEventListener('playing', check);
        resolve();
      }, 3000);
    });
  }, []);

  const startCamera = useCallback(async () => {
    if (!mountedRef.current) return;
    if (startingRef.current || startedRef.current) return;
    startingRef.current = true;
    setState('initializing');
    setErrorMessage('');

    try {
      if (!detectorRef.current) {
        // enableCache:false makes each frame an independent decode.
        // Default cache requires multiple matching reads before emitting,
        // which can suppress single-frame decodes entirely on marginal
        // symbologies.
        detectorRef.current = new BarcodeDetectorPolyfill({
          formats,
          zbar: { enableCache: false },
        });
        bumpStats({ detectorReady: true });
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      video.srcObject = stream;
      video.playsInline = true;
      video.muted = true;
      await video.play();

      // Critical on iOS Safari: video.play() can resolve before pixels
      // are available. Wait for real frame dimensions.
      await waitForVideoFrames(video);

      if (!mountedRef.current) {
        stopCamera();
        return;
      }

      bumpStats({ videoW: video.videoWidth, videoH: video.videoHeight });
      startedRef.current = true;
      setState('scanning');
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => detectLoop());
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : String(err);
      const lower = message.toLowerCase();
      if (
        lower.includes('permission') ||
        lower.includes('denied') ||
        lower.includes('notallowed')
      ) {
        setState('permission_denied');
        setErrorMessage('Camera access was denied. Please allow camera access in your browser settings.');
      } else if (lower.includes('notfound') || lower.includes('nodevices')) {
        setState('no_camera');
        setErrorMessage('No camera detected on this device');
      } else {
        setState('error');
        setErrorMessage(message || 'Failed to start camera');
      }
      onError?.(message);
    } finally {
      startingRef.current = false;
    }
  }, [formats, detectLoop, stopCamera, onError, bumpStats, waitForVideoFrames]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    activeRef.current = isActive;
    if (isActive) {
      if (!startedRef.current) {
        startCamera();
      } else {
        lastScannedRef.current = null;
        lastScanTimeRef.current = 0;
        setState('scanning');
        const video = videoRef.current;
        if (video && video.paused) {
          video.play().catch(() => {});
        }
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(() => detectLoop());
        }
      }
    } else {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const video = videoRef.current;
      if (video && !video.paused) {
        try {
          video.pause();
        } catch {
          // ignore
        }
      }
      if (startedRef.current) {
        setState('paused');
      }
    }
  }, [isActive, startCamera, detectLoop]);

  // #2210 — paint the most-recent decode region onto the overlay canvas,
  // fading it out, so the rep sees where the scanner locked on (green = MSI
  // shelf tag, blue = ZBar UPC). Runs only while scanning.
  useEffect(() => {
    if (!decodeOverlay || state !== 'scanning') return;
    let raf = 0;
    const paint = () => {
      const oc = overlayCanvasRef.current;
      const cap = canvasRef.current;
      if (oc) {
        const dispW = oc.clientWidth;
        const dispH = oc.clientHeight;
        if (oc.width !== dispW) oc.width = dispW;
        if (oc.height !== dispH) oc.height = dispH;
        const octx = oc.getContext('2d');
        if (octx) {
          octx.clearRect(0, 0, oc.width, oc.height);
          const region = decodeRegionRef.current;
          if (region && cap && cap.width > 0) {
            const age = Date.now() - region.at;
            if (age < 1100) {
              const alpha = age < 700 ? 1 : Math.max(0, 1 - (age - 700) / 400);
              const m = mapCoverBox(region, cap.width, cap.height, oc.width, oc.height);
              const rgb = region.source === 'msi' ? '34,197,94' : '59,130,246';
              octx.lineWidth = 3;
              octx.strokeStyle = `rgba(${rgb},${alpha})`;
              octx.strokeRect(m.x, m.y, m.w, m.h);
              octx.fillStyle = `rgba(${rgb},${alpha})`;
              octx.font = '600 12px system-ui, sans-serif';
              octx.fillText(`✓ ${region.value.slice(0, 24)}`, m.x, Math.max(13, m.y - 5));
            } else {
              decodeRegionRef.current = null;
            }
          }
        }
      }
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    overlayRafRef.current = raf;
    return () => {
      cancelAnimationFrame(raf);
      overlayRafRef.current = null;
      const oc = overlayCanvasRef.current;
      const octx = oc?.getContext('2d');
      if (oc && octx) octx.clearRect(0, 0, oc.width, oc.height);
    };
  }, [decodeOverlay, state]);

  const handleRetry = () => {
    stopCamera();
    startCamera();
  };

  const isError = state === 'no_camera' || state === 'permission_denied' || state === 'error';
  const isInitializing = state === 'initializing';

  return (
    <div
      style={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        borderRadius: 12,
        backgroundColor: '#000',
      }}
    >
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
        playsInline
        muted
      />

      {/* #2210 — decode-region overlay, drawn over the video. */}
      {decodeOverlay && (
        <canvas
          ref={overlayCanvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        />
      )}

      {state === 'scanning' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '85%',
            height: '40%',
            border: '2px solid rgba(255, 255, 255, 0.7)',
            borderRadius: 8,
            pointerEvents: 'none',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)',
          }}
        />
      )}

      {debug && (
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            right: 6,
            padding: '6px 8px',
            fontSize: 10,
            fontFamily: 'monospace',
            lineHeight: 1.35,
            color: '#dcfce7',
            backgroundColor: 'rgba(0,0,0,0.65)',
            borderRadius: 6,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <div>
            state={state} det={stats.detectorReady ? 'ok' : 'no'} v={stats.videoW}x{stats.videoH}
          </div>
          <div>
            frames={stats.frames} attempts={stats.attempts}
          </div>
          {stats.lastValue && (
            <div style={{ color: '#bbf7d0' }}>
              last fmt={stats.lastFormat} q={stats.lastQuality} val={stats.lastValue.slice(0, 28)}
            </div>
          )}
          {stats.lastError && (
            <div style={{ color: '#fecaca' }}>err: {stats.lastError}</div>
          )}
        </div>
      )}

      {isError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            padding: 16,
            textAlign: 'center',
          }}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {state === 'permission_denied' ? (
              <>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </>
            ) : (
              <>
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </>
            )}
          </svg>
          <p style={{ marginTop: 10, fontSize: 13, fontWeight: 500 }}>
            {state === 'no_camera' && 'No Camera Found'}
            {state === 'permission_denied' && 'Camera Access Denied'}
            {state === 'error' && 'Camera Error'}
          </p>
          <p style={{ fontSize: 11, opacity: 0.8, marginTop: 4, maxWidth: 250 }}>
            {errorMessage || 'Use manual entry below'}
          </p>
          <button
            onClick={handleRetry}
            style={{
              marginTop: 12,
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {isInitializing && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            backgroundColor: 'rgba(0,0,0,0.6)',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              border: '3px solid #334155',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 12, fontSize: 13 }}>Starting camera...</p>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default BarcodeScanner;
